import { Injectable, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { BehaviorSubject, combineLatest, firstValueFrom, type Observable } from 'rxjs';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';
import type { ModernFrameUserContextV2 } from '@platform/contracts';

import { LocalStorageService } from '../../connectors/localstorage';
import { CryptService } from '../crypt.service';
import { UsersService } from '../users.service';
import { UserBehaviorService } from '../user-events/user-behavior.service';
import { ModernFrameSessionService } from '../../core/modern-host/modern-frame-session.service';
import { ActivityService } from './activityService.service';
import { AuthService } from './authService.service';
import { MsalBootstrapService } from './msal-bootstrap.service';

interface ShellUser {
  readonly name: string | undefined;
  readonly email: string;
  readonly from: string;
}

export type CurrentUserContextStatus = 'idle' | 'loading' | 'ready' | 'failed';

@Injectable({ providedIn: 'root' })
export class CurrentUserContextService {
  readonly #activity = inject(ActivityService);
  readonly #auth = inject(AuthService);
  readonly #crypt = inject(CryptService);
  readonly #localStorage = inject(LocalStorageService);
  readonly #msal = inject(MsalService);
  readonly #msalBootstrap = inject(MsalBootstrapService);
  readonly #session = inject(ModernFrameSessionService);
  readonly #userBehavior = inject(UserBehaviorService);
  readonly #users = inject(UsersService);

  readonly #context = new BehaviorSubject<ModernFrameUserContextV2 | null>(null);
  readonly #status = new BehaviorSubject<CurrentUserContextStatus>('idle');
  readonly context$: Observable<ModernFrameUserContextV2 | null> = this.#context.asObservable();
  readonly status$: Observable<CurrentUserContextStatus> = this.#status.asObservable();
  readonly ready$: Observable<ModernFrameUserContextV2> = this.context$.pipe(
    filter((context): context is ModernFrameUserContextV2 => context !== null)
  );

  #initializationVersion = 0;
  #lastUser: ShellUser | null = null;
  #started = false;

  initialize(): void {
    if (this.#started) return;
    this.#started = true;

    this.#userBehavior.selectedUser
      .pipe(
        filter((user): user is ShellUser => !!user?.email),
        distinctUntilChanged((previous, current) =>
          previous.email === current.email && previous.name === current.name && previous.from === current.from
        )
      )
      .subscribe((user) => void this.#load(user));

    this.#session.logout$.subscribe(() => {
      this.#lastUser = null;
      this.reset();
    });
    this.#session.sessionReplaced$.subscribe(() => {
      const user = this.#lastUser;
      this.reset();
      if (user) void this.#load(user);
    });
  }

  snapshot(): ModernFrameUserContextV2 | null {
    return this.#context.getValue();
  }

  statusSnapshot(): CurrentUserContextStatus {
    return this.#status.getValue();
  }

  reset(): void {
    this.#initializationVersion += 1;
    this.#context.next(null);
    this.#status.next('idle');
  }

  async #load(user: ShellUser): Promise<void> {
    this.#lastUser = user;
    const version = ++this.#initializationVersion;
    this.#context.next(null);
    this.#status.next('loading');

    if (!(await this.#ensureExchangeCompleted()) || version !== this.#initializationVersion) {
      if (version === this.#initializationVersion && this.#status.getValue() === 'loading') {
        this.#status.next('failed');
      }
      return;
    }

    try {
      const [usersResponse, profilesResponse] = await firstValueFrom(
        combineLatest([
          this.#users.getUsersByEmail(user.email).pipe(take(1)),
          this.#users.getProfile(user.email).pipe(take(1))
        ])
      );
      if (version !== this.#initializationVersion) return;

      const userId = readUserId(usersResponse);
      const profiles = readProfiles(profilesResponse);
      if (userId === null) {
        if (version === this.#initializationVersion) this.#status.next('failed');
        return;
      }

      this.#writeLegacyStorage(userId, profiles);
      this.#context.next({
        userId,
        displayName: user.name?.trim() || user.email,
        email: user.email,
        profiles
      });
      this.#status.next('ready');
    } catch {
      if (version === this.#initializationVersion) {
        this.#context.next(null);
        this.#status.next('failed');
      }
    }
  }

  async #ensureExchangeCompleted(): Promise<boolean> {
    const sessionResult = await this.#msalBootstrap.completePortalSessionIfNeeded(this.#msal, this.#auth);
    if (sessionResult.status === 'authenticated') {
      this.#activity.setupActivityListeners();
      return true;
    }

    return false;
  }

  retry(): void {
    const user = this.#lastUser;
    if (user === null || this.#status.getValue() !== 'failed') return;
    void this.#load(user);
  }

  #writeLegacyStorage(userId: number, profiles: readonly string[]): void {
    const encryptedUserId = this.#crypt.encryptBy(userId.toString());
    const encryptedProfiles = profiles.length > 0
      ? this.#crypt.encryptArray(profiles)
      : this.#crypt.encryptBy(profiles);

    if (encryptedUserId) {
      this.#localStorage.set('userId', [encryptedUserId]);
      this.#userBehavior.emitUserIdEvent(true);
    }
    if (encryptedProfiles) this.#localStorage.set('profile', [encryptedProfiles]);
  }
}

function readUserId(value: unknown): number | null {
  const record = extractUserRecord(value);
  if (record === null) return null;

  const candidate = record['userId'] ?? record['UserID'] ?? record['UserId'];
  const userId = typeof candidate === 'number' ? candidate : Number(candidate);
  return Number.isSafeInteger(userId) && userId > 0 ? userId : null;
}

function readProfiles(value: unknown): readonly string[] {
  if (value === null || value === undefined) return [];

  const profile = typeof value === 'object' && value !== null && 'profile' in value
    ? (value as { profile?: unknown }).profile
    : value;

  if (typeof profile === 'string' && profile.trim().length > 0) {
    return [profile.trim()];
  }

  if (!Array.isArray(profile)) return [];

  return profile.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
}

function extractUserRecord(value: unknown): Record<string, unknown> | null {
  if (Array.isArray(value)) {
    const first = value[0];
    return typeof first === 'object' && first !== null ? (first as Record<string, unknown>) : null;
  }

  if (typeof value === 'object' && value !== null) {
    return value as Record<string, unknown>;
  }

  return null;
}

export { readProfiles, readUserId };
